import { BLOOD_PRESSURE, BODY_MASS_INDEX, SMOKING } from "constants/trend-charts";
import { theme } from "@sisuwellness/web-components";
import calculatePointColor from "utilities/trend-chart/calculateGuldeline";
import { METRIC_INDICATOR } from "utilities/metrics/mapIndicators";
import { PORTAL_IMAGE } from "constants/imagePath";
import { fixedNumber } from "utilities/commonUtils";
import formatTargetWeightRange from "@sisuwellness/utilities/Guidelines/Weight/formatTargetWeightRange";
import i18n from "../../../../i18n.js";

/**
 * Get metric details specifically for heart age page
 * @param {string} type
 * @param {object} datum
 * @return {{} | { icon: string, title: string, value: string | number, unit: string, label: string, color: string, recommendation: string}}
 */
export const getMetricDetails = (type, datum) => {
    const { themeKey } = METRIC_INDICATOR[type] || {};

    const themeColor = theme.guidelines[themeKey];

    const { key = "" } = calculatePointColor(type, datum) || {};

    switch (type) {
        case BODY_MASS_INDEX: {
            const title = i18n.t("words.bmi");
            let metric = {};

            const { bmi, height, nhsEthnicity, ireEthnicity } = datum;
            // Ethnicity-aware healthy weight band, sourced from the citizen-biased datum.
            // Heart Age's own keys (members_portal_web.views.heart_age.*.bmi.*) carry no
            // weight tokens today — their copy is about heart-age impact, not a target. The
            // interpolation is passed anyway so the keys can adopt the tokens later without
            // a code change; i18next ignores unused interpolation values.
            const targetWeightRange = formatTargetWeightRange(height, nhsEthnicity || ireEthnicity, false);
            const bmiInterpolation = {
                HEALTHY_WEIGHT_LOWER: targetWeightRange.lower,
                HEALTHY_WEIGHT_UPPER: targetWeightRange.upper
            };

            switch (key) {
                case "low":
                    metric = {
                        label: i18n.t("ranges.low"),
                        color: themeColor.low.sisuPortal.hex,
                        recommendation: i18n.t(
                            "members_portal_web.views.heart_age.page.affects.utilities.metrics.recommendations.bmi.low",
                            bmiInterpolation
                        )
                    };
                    break;

                case "normal":
                    metric = {
                        label: i18n.t("ranges.normal"),
                        color: themeColor.normal.sisuPortal.hex,
                        recommendation: i18n.t(
                            "members_portal_web.views.heart_age.page.affects.utilities.metrics.recommendations.bmi.normal",
                            bmiInterpolation
                        )
                    };
                    break;

                case "high":
                case "elevated":
                    metric = {
                        label: i18n.t("ranges.intermediate"),
                        color: themeColor.elevated.sisuPortal.hex,
                        recommendation: i18n.t(
                            "members_portal_web.views.heart_age.page.affects.utilities.metrics.recommendations.bmi.intermediate",
                            bmiInterpolation
                        )
                    };
                    break;

                case "veryHigh":
                    metric = {
                        label: i18n.t("ranges.very_high"),
                        color: themeColor.high.sisuPortal.hex,
                        recommendation: i18n.t(
                            "members_portal_web.views.heart_age.page.affects.utilities.metrics.recommendations.bmi.very_high",
                            bmiInterpolation
                        )
                    };
                    break;
            }

            return {
                ...metric,
                title,
                unit: i18n.t("units.uc_bmi"),
                value: fixedNumber(bmi),
                icon: `${PORTAL_IMAGE}/healthHub/bmi.svg`
            };
        }

        case BLOOD_PRESSURE: {
            const title = i18n.t("words.blood_pressure");
            let metric = {};

            const { sys, dia } = datum;

            switch (key) {
                case "low":
                    metric = {
                        label: i18n.t("ranges.low"),
                        color: themeColor.low.sisuPortal.hex,
                        recommendation: i18n.t(
                            "members_portal_web.views.heart_age.page.affects.utilities.metrics.recommendations.blood_pressure.low"
                        )
                    };
                    break;

                case "normal":
                    metric = {
                        label: i18n.t("ranges.normal"),
                        color: themeColor.normal.sisuPortal.hex,
                        recommendation: i18n.t(
                            "members_portal_web.views.heart_age.page.affects.utilities.metrics.recommendations.blood_pressure.normal"
                        )
                    };
                    break;

                case "highNormal":
                    metric = {
                        label: i18n.t("ranges.high_normal"),
                        color: themeColor.highNormal.sisuPortal.hex,
                        recommendation: i18n.t(
                            "members_portal_web.views.heart_age.page.affects.utilities.metrics.recommendations.blood_pressure.high_normal"
                        )
                    };
                    break;

                case "high":
                    metric = {
                        label: i18n.t("ranges.high"),
                        color: themeColor.high.sisuPortal.hex,
                        recommendation: i18n.t(
                            "members_portal_web.views.heart_age.page.affects.utilities.metrics.recommendations.blood_pressure.high"
                        )
                    };
                    break;

                case "severe":
                    metric = {
                        label: i18n.t("ranges.severe"),
                        color: themeColor.severe.sisuPortal.hex,
                        recommendation: i18n.t(
                            "members_portal_web.views.heart_age.page.affects.utilities.metrics.recommendations.blood_pressure.severe"
                        )
                    };
                    break;
            }
            return {
                ...metric,
                title,
                unit: i18n.t("units.mmhg"),
                value: `${sys}/${dia}`,
                icon: `${PORTAL_IMAGE}/healthHub/bloodpressure.svg`
            };
        }

        case SMOKING: {
            const title = "Smoking";
            let metric = {};

            const { isSmoker } = datum;

            switch (key) {
                case "normal":
                    metric = {
                        label: i18n.t("words.great"),
                        color: themeColor.normal.sisuPortal.hex,
                        recommendation: i18n.t(
                            "members_portal_web.views.heart_age.page.affects.utilities.metrics.recommendations.smoking.great"
                        )
                    };
                    break;

                case "high":
                    metric = {
                        label: i18n.t("words.risk"),
                        color: themeColor.high.sisuPortal.hex,
                        recommendation: i18n.t(
                            "members_portal_web.views.heart_age.page.affects.utilities.metrics.recommendations.smoking.risk"
                        )
                    };
                    break;
            }
            return {
                ...metric,
                title,
                value: isSmoker ? i18n.t("words.yes") : i18n.t("words.not_smoking"),
                icon: `${PORTAL_IMAGE}/healthHub/smoking.svg`
            };
        }

        case "DIABETIC": {
            const title = i18n.t("words.diabetes");
            let metric = {};

            const { hasDiabetes } = datum;

            const isDiabetic = hasDiabetes === "diabetic";

            if (isDiabetic)
                metric = {
                    label: i18n.t("ranges.high_risk"),
                    color: theme.colours.hpPrimaryRed.hex,
                    recommendation: i18n.t(
                        "members_portal_web.views.heart_age.page.affects.utilities.metrics.recommendations.diabetic.high_risk"
                    )
                };
            else
                metric = {
                    label: i18n.t("ranges.normal"),
                    color: theme.colours.hpGreen.hex,
                    recommendation: i18n.t(
                        "members_portal_web.views.heart_age.page.affects.utilities.metrics.recommendations.diabetic.normal"
                    )
                };

            return {
                ...metric,
                title,
                value: isDiabetic ? i18n.t("words.diagnosed") : i18n.t("words.no"),
                icon: `${PORTAL_IMAGE}/premium/diabetic.svg`
            };
        }

        default:
            return {};
    }
};
